import { faInfoCircle } from "@fortawesome/pro-regular-svg-icons";
import { Controller, useFormContext, useFormState } from "react-hook-form";
import slugify from "slugify";

import { Card } from "src/base-components/Card";
import { Input } from "src/base-components/Input";
import { Label } from "src/base-components/Label";
import { TemplateDescriptionEditor } from "src/cases/templates/common/TemplateDescriptionEditor";
import { CaseTemplateFormValues } from "src/cases/templates/formUtils/types";
import { useIsTemplateVersionPublished } from "src/cases/templates/utils";
import { ErrorHint } from "src/design-system/ErrorHint";

type ItemType = "action" | "task";

type TemplateItemInfoProps = {
  itemIndex: number;
  itemType: ItemType;
  descriptionPlaceholder?: string;
  nameDisabled?: boolean;
  keyDisabled?: boolean;
  descriptionDisabled?: boolean;
};

export const TemplateItemInfo: React.FC<TemplateItemInfoProps> = ({
  itemIndex,
  itemType,
  descriptionPlaceholder = "Write a description for the reviewer",
  nameDisabled = false,
  keyDisabled = false,
  descriptionDisabled = false,
}) => {
  const form = useFormContext<CaseTemplateFormValues>();
  const { errors } = useFormState<CaseTemplateFormValues>();
  const isPublished = useIsTemplateVersionPublished();

  const fieldPath = itemType === "action" ? "case_actions" : "case_tasks";

  const itemErrors =
    itemType === "action"
      ? errors.attributes?.case_actions?.[itemIndex]
      : errors.attributes?.case_tasks?.[itemIndex];

  return (
    <Card className="w-full">
      <div className="flex flex-col gap-y-3">
        <div className="flex flex-1 flex-col">
          <Label required>Name</Label>
          <Input
            data-loc={`${itemType}-name-input`}
            disabled={nameDisabled}
            errored={!!itemErrors?.display_name}
            placeholder="Name"
            fullWidth
            {...form.register(
              `attributes.${fieldPath}.${itemIndex}.display_name`,
              {
                onChange: (event) => {
                  const { isDirty } = form.getFieldState(
                    `attributes.${fieldPath}.${itemIndex}.key`,
                  );

                  if (!isDirty && !isPublished) {
                    form.setValue(
                      `attributes.${fieldPath}.${itemIndex}.key`,
                      slugify(event.target.value, {
                        lower: true,
                        strict: true,
                        replacement: "_",
                      }),
                      { shouldValidate: true },
                    );
                  }
                },
                deps: `attributes.${fieldPath}.${itemIndex}.key`,
              },
            )}
          />
          {itemErrors?.display_name && (
            <ErrorHint>{itemErrors.display_name.message}</ErrorHint>
          )}
        </div>

        <div className="flex flex-1 flex-col">
          <Label required>Key</Label>
          <Input
            data-loc={`${itemType}-key-input`}
            disabled={keyDisabled}
            errored={!!itemErrors?.key}
            placeholder="Key"
            fullWidth
            monospaced
            {...form.register(`attributes.${fieldPath}.${itemIndex}.key`, {
              deps: `attributes.${fieldPath}.${itemIndex}.display_name`,
            })}
            suffixIcon={{
              icon: faInfoCircle,
              helpText:
                "A unique identifier for this field, to be referenced in the Decision Flow.",
            }}
          />
          {itemErrors?.key && <ErrorHint>{itemErrors.key.message}</ErrorHint>}
        </div>

        <div className="flex flex-1 flex-col">
          <Label>Describe the {itemType}</Label>
          <Controller
            control={form.control}
            name={`attributes.${fieldPath}.${itemIndex}.description`}
            render={({ field, fieldState }) => (
              <>
                <TemplateDescriptionEditor
                  data-loc={`${itemType}-description-editor`}
                  disabled={descriptionDisabled}
                  placeholder={descriptionPlaceholder}
                  value={field.value ?? ""}
                  onChange={(value) => {
                    field.onChange(value);
                    form.trigger(
                      `attributes.${fieldPath}.${itemIndex}.description`,
                    );
                  }}
                />
                {fieldState.error && (
                  <ErrorHint>{fieldState.error.message}</ErrorHint>
                )}
              </>
            )}
          />
        </div>
      </div>
    </Card>
  );
};
